import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { fmtDate, nok, num } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Eased count-up (cubic out) used for the headline numbers. Snaps to the target when motion is reduced. */
const useCountUp = (target, enabled, duration = 1100, delay = 350) => {
  const [v, setV] = useState(enabled ? 0 : target);
  useEffect(() => {
    if (!enabled || !Number.isFinite(target)) { setV(target); return undefined; }
    let raf;
    let t0;
    const tick = (now) => {
      if (t0 == null) t0 = now;
      const p = Math.min(1, Math.max(0, (now - t0 - delay) / duration));
      const e = 1 - Math.pow(1 - p, 3);
      setV(target * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, enabled, duration, delay]);
  return v;
};

const Fact = ({ label, value, format, unit, sub, testId, i, reduce, className }) => {
  const n = useCountUp(value, !reduce);
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.06 }}
      className={cn("min-w-0 bg-card px-5 py-5 sm:px-6 sm:py-6", className)}
      data-testid={testId}
    >
      <div className="text-[12.5px] text-muted-foreground">{label}</div>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5">
        <span className="font-mono text-[22px] font-medium tabular leading-none tracking-tight sm:text-[24px]" data-value={value}>{format(n)}</span>
        {unit && <span className="text-[13px] text-muted-foreground">{unit}</span>}
      </div>
      {sub && <div className="mt-1.5 text-[12.5px] leading-5 text-muted-foreground">{sub}</div>}
    </motion.div>
  );
};

/** Floating key-facts strip that overlaps the cover: capacity, modules, rental period, monthly, establishment. */
export const ProposalFacts = ({ data }) => {
  const reduce = useReducedMotion();
  const sol = data.solution;
  const p = data.project || {};
  const pr = data.pricing;
  const money = (v) => nok(Math.round(v), { bare: true });
  const facts = [];
  if (sol?.kpi?.beds) facts.push({ label: "Kapasitet", value: sol.kpi.beds, format: (v) => num(Math.round(v)), unit: "senger", sub: p.capacity_persons ? `Behov: ${num(p.capacity_persons)} personer` : null, testId: "proposal-fact-capacity" });
  else if (p.capacity_persons) facts.push({ label: "Kapasitet", value: p.capacity_persons, format: (v) => num(Math.round(v)), unit: "personer", sub: null, testId: "proposal-fact-capacity" });
  if (sol?.kpi?.modules) facts.push({ label: "Moduler", value: sol.kpi.modules, format: (v) => num(Math.round(v)), unit: null, sub: [sol.kpi.floors ? `${sol.kpi.floors} ${sol.kpi.floors === 1 ? "etasje" : "etasjer"}` : null, sol.kpi.area_m2 ? `${num(sol.kpi.area_m2)} m²` : null].filter(Boolean).join(" · "), testId: "proposal-fact-modules" });
  if (data.duration_months) facts.push({ label: "Leieperiode", value: data.duration_months, format: (v) => num(v, 1), unit: "mnd", sub: p.start_date && p.end_date ? `${fmtDate(p.start_date)} – ${fmtDate(p.end_date)}` : p.start_date ? `Fra ${fmtDate(p.start_date)}` : null, testId: "proposal-fact-duration" });
  if (pr.monthly_total) facts.push({ label: "Månedlig leie", value: pr.monthly_total, format: money, unit: "kr", sub: "eks. mva", testId: "proposal-fact-monthly" });
  if (pr.establishment_total) facts.push({ label: "Etablering", value: pr.establishment_total, format: money, unit: "kr", sub: "engangskostnad eks. mva", testId: "proposal-fact-establishment" });
  if (facts.length < 4) facts.push({ label: "Sum eks. mva", value: pr.revenue, format: money, unit: "kr", sub: `${nok(pr.total_incl_vat)} inkl. mva`, testId: "proposal-fact-total" });
  const shown = facts.slice(0, 5);
  const cols = shown.length === 5 ? "md:grid-cols-5" : shown.length === 4 ? "md:grid-cols-4" : "md:grid-cols-3";
  const odd = shown.length % 2 === 1;

  return (
    <div className="relative z-10 mx-auto -mt-12 max-w-[1180px] px-5 sm:px-8" data-testid="proposal-facts">
      <div className={`grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-border/70 proposal-shadow ring-1 ring-border/60 ${cols}`}>
        {shown.map((f, i) => <Fact key={f.testId} {...f} i={i} reduce={reduce} className={odd && i === shown.length - 1 ? "col-span-2 md:col-span-1" : ""} />)}
      </div>
    </div>
  );
};
